import React, { useState, useEffect } from 'react';
import OfficeInfo, { IOfficeInfoProps } from './OfficeInfo';
import { useTranslation } from 'react-i18next';
import Modal from 'tbk-components/src/components/Modal';
import Image from 'tbk-components/src/components/Image';
import calianLogo from '../../../assets/images/logo-colour.svg';
import { groupBy, mapValues, reduce, isEmpty } from 'lodash';

export interface IDistributorCardProps {
	post_title?: string;
	country_info_override?: any[];
	/**
	 * Office list
	 */
	partner_info?: IOfficeInfoProps;
	/**
	 * Distributor introduction
	 */
	post_content?: string;
	/**
	 * @noUI
	 */
	region?: string;
	/**
	 * @noUI
	 */
	country?: string;
	gold_partner?: boolean;
	stock_distributor?: boolean;
	i18n?: {
		goldPartners?: string;
		stockDistributors?: string;
	};
}

/**
 * Calian Distributor Card Component
 * @icon welcome-widgets-menus
 * @block
 */
const DistributorCard: React.FC<IDistributorCardProps> = ({
	post_title,
	country_info_override = [],
	partner_info,
	post_content,
	region,
	country,
	gold_partner,
	stock_distributor,
	i18n,
}) => {
	const [isAdmin, setIsAdmin] = useState(false);

	useEffect(() => {
		//@ts-ignore
		if (window.wp && window.wp.apiFetch) {
			setIsAdmin(true);
		}
	}, []);
	const [showModal, setShowModal] = useState(false);
	const [overrideInfo, setOverrideInfo] = useState(null);
	const { t } = useTranslation();
	const cardHeader = (gold_partner || stock_distributor) && (
		<div className="distributor-card__header">
			{gold_partner && (
				<span className="distributor-card__gold-partner">
					{i18n?.goldPartners || t('Gold Precision Partner')}
				</span>
			)}
			{stock_distributor && (
				<span className="distributor-card__stock-distributor">
					{i18n?.stockDistributors || t('Stock Distributor')}
				</span>
			)}
		</div>
	);

	useEffect(() => {
		if (
			country_info_override?.length < 2 &&
			country_info_override?.length > 0 &&
			!country_info_override[0]['country']
		)
			return;

		const groupedByCountry = groupBy(country_info_override, 'country');

		// Transform each group into the desired structure
		const result = mapValues(groupedByCountry, (entries) => {
			return reduce(
				entries,
				(acc, entry) => {
					acc[entry.key] = entry.value;
					return acc;
				},
				{},
			);
		});

		if (!isEmpty(result)) setOverrideInfo(result);
	}, [country_info_override]);

	return (
		<>
			<div
				className="distributor-card__container"
				tabIndex={0}
				aria-label={post_title}
				onClick={() => {
					if (!isAdmin) {
						setShowModal((s) => !s);
					}
				}}
				onKeyDown={(e) => {
					if (!isAdmin && e.key === 'Enter') setShowModal((s) => !s);
				}}
			>
				<div className="distributor-card__container-inner">
					{cardHeader}
					<div className="distributor-card__body">
						{post_title && (
							<span className="distributor-card__title">
								{post_title}
							</span>
						)}
						<div className="distributor-card__head-office">
							{<OfficeInfo {...partner_info} />}
						</div>
						{post_content && (
							<div
								className="distributor-card__intro"
								dangerouslySetInnerHTML={{
									__html: post_content,
								}}
							/>
						)}
					</div>
					<div className="distributor-card__footer">
						<span className="icon-arrow-right1" />
					</div>
				</div>
			</div>
			{showModal && (
				<Modal
					className="distributor-card__modal"
					onCancel={() => {
						setShowModal(false);
					}}
				>
					<div
						className={`distributor-card__modal__container py-5 pt-7 px-3 p-md-7 p-lg-9 px-xl-15`}
					>
						<div className="distributor-card__modal__container-inner">
							<div className="distributor-card__modal__left">
								{cardHeader}
								{post_title && (
									<span className="distributor-card__title">
										{post_title}
									</span>
								)}
								{post_content && (
									<div
										className="distributor-card__intro"
										dangerouslySetInnerHTML={{
											__html: post_content,
										}}
									/>
								)}
								<div className="distributor-card__modal__logo">
									<Image
										src={calianLogo}
										width={180}
										height={65}
									/>
								</div>
							</div>
							<div className="distributor-card__modal__right">
								<div>
									{
										<OfficeInfo
											{...partner_info}
											showAsHeadOffice={
												country_info_override?.length >
													0 &&
												country_info_override[0][
													'country'
												]
											}
										/>
									}
								</div>
								{overrideInfo &&
									Object.keys(overrideInfo).length >= 1 &&
									Object.keys(overrideInfo).map(
										(country, index) => (
											<OfficeInfo
												key={index}
												location={country}
												toll_free={
													overrideInfo[country]
														.toll_free
												}
												telephone={
													overrideInfo[country]
														.telephone
												}
												fax={overrideInfo[country].fax}
												email={
													overrideInfo[country].email
												}
											/>
										),
									)}
							</div>
						</div>
					</div>
				</Modal>
			)}
		</>
	);
};

export default DistributorCard;
